import mixins from 'vue-typed-mixins';
import { AxiosResponse } from 'axios';
import _ from 'lodash';
import { ErrorResponse } from '@codequality/vue-cli-base';

import { E入力分類 } from '@/openapi/enum';
import { データApi, データRequest, E金融借入科目, E金融借入種別, E金融借入返済方法 } from '@/openapi/user';

import C金融機関借入EditRow from '@/entries/user/pages/authorized/事業所/計画書/分析計画/借入/金融機関借入/金融機関借入EditRow/金融機関借入EditRow.vue';
import AlertMixin from '@/mixins/AlertMixin';
import ListMixin from '@/entries/user/mixins/ListMixin';
import RoutesMixin from '@/entries/user/mixins/RoutesMixin';
import 計画書Mixin from '@/entries/user/mixins/計画書Mixin';
import フェーズ種別Mixin from '@/entries/user/mixins/フェーズ種別Mixin';

import UserApiUtility from '@/classes/common/UserApiUtility';
import 金融機関借入Service, {
  金融機関借入,
  金融機関借入ListItem,
} from '@/classes/service/分析計画/金融機関借入Service';
import 返済明細Service from '@/classes/service/計画/返済明細Service';
import OldLoanMixin from '@/entries/user/mixins/OldLoanMixin';

const initialEditItem = (): 金融機関借入ListItem => {
  return {
    金融機関: '',
    種別: E金融借入種別.証書貸付,
    科目: E金融借入科目.短期借入金,
    返済方法: E金融借入返済方法.元金均等,
    実行日: '',
    返済期日: '',
    信用保証協会付: '',
    保証人: '',
    備考: '',
    返済額: [],
    元本返済: [],
    利息: [],
  };
};

export default mixins(AlertMixin, ListMixin, RoutesMixin, 計画書Mixin, フェーズ種別Mixin, OldLoanMixin).extend({
  name: 'C金融機関借入',

  components: {
    C金融機関借入EditRow,
  },

  data(): {
    service: 金融機関借入Service;
    返済明細Service: 返済明細Service;
    api: データApi;
    v金融機関借入: 金融機関借入 | null;
    new金融機関借入: 金融機関借入ListItem;
    edit金融機関借入: 金融機関借入ListItem;
    reload: number;
  } {
    return {
      service: new 金融機関借入Service(),
      api: UserApiUtility.ogApi(データApi),
      v金融機関借入: null,
      new金融機関借入: initialEditItem(),
      edit金融機関借入: initialEditItem(),
      返済明細Service: new 返済明細Service(),
      reload: 0,
    };
  },

  created(): void {
    this.$_load();
  },

  methods: {
    async $_load(): Promise<void> {
      const response = await this.api.listデータ(
        this.計画書.pk,
        this.service.getRelationalE入力分類s(),
        this.apiフェーズ種別,
        this.mockApiOptions('金融機関借入', true)
      );
      this.v金融機関借入 = this.service.parse(response.data);
      const 返済明細 = this.返済明細Service.parse(response.data);
      const oldData = this.返済明細Service.createTデータOldLoans(response.data);
      this.getOldLoans(oldData, 返済明細);
    },

    selectForEdit(v借入: 金融機関借入ListItem): void {
      this.edit金融機関借入 = _.cloneDeep(v借入);
    },

    clearForm(isNew: boolean): void {
      const init = initialEditItem();
      if (isNew) {
        this.new金融機関借入 = init;
        this.reload++;
      } else {
        this.edit金融機関借入 = init;
      }
    },

    async save(isNew: boolean, editItem: 金融機関借入ListItem): Promise<void> {
      // 入力チェック
      if (editItem.返済方法 === E金融借入返済方法.元利均等) {
        const keys: (keyof 金融機関借入ListItem)[] = ['借入金額', '返済回数', '当初金利'];
        for (const key of keys) {
          if (editItem[key] === undefined) {
            alert('返済方法が「元利均等」の場合、' + key + 'は入力必須です。');
            return;
          }
        }
      }

      const { データpk, 番号, ...金融機関借入 } = editItem;

      const newGroup = this.v金融機関借入?.list
        .filter((item) => isNew || item.データpk !== データpk)
        .find((item) => item.金融機関 === 金融機関借入.金融機関 && item.科目 === 金融機関借入.科目);
      if (newGroup !== undefined) {
        金融機関借入.返済額 = newGroup.返済額;
      } else if (isNew) {
        金融機関借入.返済額 = [];
      } else {
        const org = this.v金融機関借入?.list.find((item) => item.データpk === データpk);
        const orgGroup = this.v金融機関借入?.list
          .filter((item) => item.データpk !== データpk)
          .find((item) => item.金融機関 === org?.金融機関 && item.科目 === org?.科目);
        if (orgGroup !== undefined) 金融機関借入.返済額 = [];
      }

      const request: データRequest = {
        c入力分類: E入力分類.金融機関借入,
        c番号: 番号!,
        cフェーズ種別: this.eフェーズ種別,
        cデータ: JSON.stringify(金融機関借入),
      };
      this.updateRequestSave(request);

      const response: AxiosResponse<void | ErrorResponse> = isNew
        ? await this.api.createデータ(this.計画書.pk, request)
        : await this.api.updateデータ(データpk!, request);

      if (this.isValidationErrorResponse(response)) return;
      this.$_clearAndLoad();
    },

    async remove(データpk: number): Promise<void> {
      const isCancelOrError = await this.isCancelOrValidationErrorResponseToDelete(() =>
        this.api.deleteデータ(データpk)
      );
      if (isCancelOrError) return;
      this.$_clearAndLoad();
    },

    $_clearAndLoad() {
      this.clearForm(true);
      this.clearForm(false);
      this.$_load();
    },
  },
});
